import React, { useState } from 'react';
import { useShop } from '../context/ShopContext';
import { MapPin, Clock, Mail, Phone, Lock, Facebook, ShieldCheck, Heart, Sparkles } from 'lucide-react';
import { Logo } from './Logo';
import { BrandGuidelinesModal } from './BrandGuidelinesModal';

export const Footer: React.FC = () => {
  const { setActivePage, settings } = useShop();
  const [showBrandModal, setShowBrandModal] = useState(false);

  return (
    <footer className="bg-[#001038] text-slate-300 border-t border-blue-950 pt-16 pb-12 mt-20 print:hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          
          {/* Brand Info & Mission */}
          <div className="space-y-4">
            <div
              onClick={() => setShowBrandModal(true)}
              className="bg-white/95 p-3 rounded-2xl inline-block shadow-lg border border-white/20 cursor-pointer hover:bg-white hover:scale-105 transition-all group"
              title="Click to view Brand System & Guidelines"
            >
              <Logo variant="horizontal" size="md" showTagline={false} />
            </div>

            <p className="text-xs text-sky-200/90 font-bold tracking-wide">
              Giving School Uniform a Second Life
            </p>

            <p className="text-xs text-slate-300 leading-relaxed">
              Free School Uniform for Immingham Families. Based at Bert Boyden Community Centre, Immingham. Helping local families save money while keeping quality school uniforms in circulation.
            </p>

            <div className="pt-2">
              <a
                href={settings.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600/30 text-blue-200 border border-blue-400/30 hover:bg-blue-600 hover:text-white text-xs font-bold transition shadow-sm"
              >
                <Facebook className="w-4 h-4 fill-current" />
                <span>Facebook Community Page</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-3">
            <h4 className="font-heading font-bold text-white text-xs tracking-wider uppercase">Quick Navigation</h4>
            <ul className="space-y-2 text-xs font-medium">
              <li>
                <button onClick={() => { setActivePage('shop'); window.scrollTo(0, 0); }} className="hover:text-sky-300 transition">
                  Browse Available Uniform
                </button>
              </li>
              <li>
                <button onClick={() => { setActivePage('manage-reservation'); window.scrollTo(0, 0); }} className="hover:text-sky-300 transition">
                  Find My Reservation
                </button>
              </li>
              <li>
                <button onClick={() => { setActivePage('donate'); window.scrollTo(0, 0); }} className="hover:text-sky-300 transition">
                  Donate School Uniform
                </button>
              </li>
              <li>
                <button onClick={() => { setActivePage('how-it-works'); window.scrollTo(0, 0); }} className="hover:text-sky-300 transition">
                  How It Works
                </button>
              </li>
              <li>
                <button onClick={() => { setActivePage('news'); window.scrollTo(0, 0); }} className="hover:text-sky-300 transition">
                  News &amp; Volunteer Appeals
                </button>
              </li>
              <li>
                <button onClick={() => { setActivePage('about'); window.scrollTo(0, 0); }} className="hover:text-sky-300 transition">
                  Our Community Mission
                </button>
              </li>
              <li>
                <button onClick={() => { setActivePage('faq'); window.scrollTo(0, 0); }} className="hover:text-sky-300 transition">
                  Frequently Asked Questions
                </button>
              </li>
            </ul>
          </div>

          {/* Collection Point & Hours */}
          <div className="space-y-3">
            <h4 className="font-heading font-bold text-white text-xs tracking-wider uppercase">Bert Boyden Hub</h4>
            <div className="space-y-2.5 text-xs text-slate-300">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-white">Bert Boyden Community Centre</p>
                  <p className="text-slate-300">{settings.collectionAddress}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-white">Opening Times</p>
                  <p className="text-slate-300">{settings.openingHours}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{settings.contactEmail}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-blue-400 shrink-0" />
                <span>{settings.contactPhone}</span>
              </div>
            </div>
          </div>

          {/* Community Partners & Privacy */}
          <div className="space-y-4">
            <h4 className="font-heading font-bold text-white text-xs tracking-wider uppercase">Community Partners</h4>
            <div className="p-4 rounded-2xl bg-blue-950/60 border border-blue-800/40 space-y-2">
              <div className="flex items-center gap-2 text-emerald-300 font-bold text-xs">
                <ShieldCheck className="w-4 h-4" />
                <span>100% Free &amp; Confidential</span>
              </div>
              <p className="text-[11px] text-slate-300 leading-normal">
                Supported by Immingham schools, local volunteers, and Bert Boyden Community Centre.
              </p>
            </div>

            <div className="flex flex-col gap-2 pt-1 text-xs">
              <div className="flex flex-wrap items-center gap-2.5 text-slate-400 font-medium">
                <button onClick={() => { setActivePage('privacy'); window.scrollTo(0, 0); }} className="hover:text-white transition">
                  Privacy Policy
                </button>
                <span>•</span>
                <button onClick={() => { setActivePage('terms'); window.scrollTo(0, 0); }} className="hover:text-white transition">
                  Terms of Service
                </button>
                <span>•</span>
                <button onClick={() => setShowBrandModal(true)} className="hover:text-sky-300 transition flex items-center gap-1 text-sky-400 font-bold">
                  <Sparkles className="w-3 h-3" />
                  <span>Brand System</span>
                </button>
              </div>

              <button
                onClick={() => { setActivePage('admin'); window.scrollTo(0, 0); }}
                className="flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-amber-300 transition pt-1"
              >
                <Lock className="w-3.5 h-3.5 text-amber-400" />
                <span>Volunteer Hub Login</span>
              </button>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="border-t border-blue-950 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <p>© {new Date().getFullYear()} Uniform Loop. Giving School Uniform a Second Life at Bert Boyden Community Centre, Immingham.</p>
          <p className="font-medium text-sky-300/90 flex items-center gap-1.5">
            <Heart className="w-3.5 h-3.5 fill-sky-400 text-sky-400" />
            <span>Community driven • Volunteer powered • Sustainable</span>
          </p>
        </div>
      </div>

      {/* Brand Guidelines Modal */}
      <BrandGuidelinesModal isOpen={showBrandModal} onClose={() => setShowBrandModal(false)} />
    </footer>
  );
};
